import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule } from '@jsverse/transloco';
import { UserRole } from '@markant-platform/shared/user/model';
import { UserCartridgeRoleAssignment } from '@markant-platform/authorizer/model';
import { AuthorizerService } from '@markant-platform/authorizer/data-access';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { CartridgeRoleAssignmentComponent } from '../cartridge-role-assignment/cartridge-role-assignment.component';
import { CartridgeRoleAssignmentDataService } from '@markant-platform/shared/authorizer-utils';

@Component({
  selector: 'markant-platform-service-roles-assignments-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    TranslocoModule,
    TranslocoModule,
    MarkantElementsModule,
    CartridgeRoleAssignmentComponent,
  ],
  templateUrl: './service-roles-assignments-dialog.component.html',
  styleUrl: './service-roles-assignments-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceRolesAssignmentsDialogComponent implements OnInit {
  @Input({ required: true }) organizationId!: string;
  @Input() previousCartridgeRoleAssignments!: Record<string, UserRole[]>;
  @Input() userId?: string;
  @Input() groupId?: string;
  @Input() groupName?: string;

  @Output() closeDialog = new EventEmitter<void>();
  @Output() reloadRoleAssignments = new EventEmitter<void>();

  selectedCartridgeRoles = computed(() => this.cartridgeRoleAssignmentDataService.selectedRoles());
  allCurrentlySelectedRoles: UserCartridgeRoleAssignment[] = [];
  allPreviouslySelectedRoles: UserCartridgeRoleAssignment[] = [];
  loading = false;

  private readonly authorizer = inject(AuthorizerService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly cartridgeRoleAssignmentDataService = inject(CartridgeRoleAssignmentDataService);

  ngOnInit(): void {
    const cartridgeRoleAssignments = this.convertCartridgeRoleAssignments(
      this.cartridgeRoleAssignmentDataService.cartridgeRolesAssignments()
    );
    this.allPreviouslySelectedRoles = cartridgeRoleAssignments;
    this.allCurrentlySelectedRoles = cartridgeRoleAssignments;
  }

  submitRoleAssignments(): void {
    this.transformCurrentlySelectedCartridgeRoles();
    const addedRoles = this.filterAddedRoles();
    const removedRoles = this.filterRemovedRoles();
    this.loading = true;

    this.callRemoveRoles(removedRoles, 0)
      .pipe(switchMap(errors => this.callAddRoles(addedRoles, 0, errors)))
      .subscribe(errors => {
        this.handleMultipleErrorResponses(addedRoles.length + removedRoles.length, errors);
        this.reloadRoleAssignments.emit();
        this.closeDialog.emit();
        this.loading = false;
      });
  }

  private convertCartridgeRoleAssignments(
    previousCartridgeRoleAssignments: Record<string, UserRole[]>
  ): UserCartridgeRoleAssignment[] {
    return Object.values(previousCartridgeRoleAssignments)
      .flatMap(userRoles => userRoles)
      .map(
        ({ id, leistungId, suborganizationId }) =>
          ({
            roleId: id,
            organizationId: this.organizationId,
            leistungId: leistungId,
            suborganizationId: suborganizationId,
          }) as UserCartridgeRoleAssignment
      );
  }

  private handleMultipleErrorResponses(expectedNumber: number, errors: Error[]): void {
    if (expectedNumber === 0) {
      return;
    }
    if (errors.length === 0) {
      return this.snackbarService.showSuccessSnackbarWithTransloco('user.details.role-assignment.updateSuccess');
    }
    if (expectedNumber === errors.length) {
      return this.snackbarService.showErrorSnackbarWithTransloco('user.details.role-assignment.updateError');
    }
    return this.snackbarService.showErrorSnackbarWithTransloco('user.details.role-assignment.partialSuccess', {
      total: expectedNumber.toString(),
      errors: errors.length.toString(),
    });
  }

  private transformCurrentlySelectedCartridgeRoles(): void {
    this.allCurrentlySelectedRoles = [];
    this.selectedCartridgeRoles().forEach(cartridgeWithRoles => {
      cartridgeWithRoles.roles.forEach(role => {
        if (role.suborganizationData && role.suborganizationData.length > 0) {
          role.suborganizationData?.forEach(suborganization => {
            this.allCurrentlySelectedRoles.push({
              roleId: role.id,
              organizationId: this.organizationId,
              leistungId: cartridgeWithRoles.id,
              suborganizationId: suborganization.id,
            });
          });
        } else {
          this.allCurrentlySelectedRoles.push({
            roleId: role.id,
            organizationId: this.organizationId,
            leistungId: cartridgeWithRoles.id,
            suborganizationId: undefined,
          });
        }
      });
    });
  }

  private callAddRoles(
    addedRoles: UserCartridgeRoleAssignment[],
    i: number,
    errorList: Error[] = []
  ): Observable<Error[]> {
    if (addedRoles.length <= i) {
      return of(errorList);
    }
    const suborganization = addedRoles[i].suborganizationId ? addedRoles[i].suborganizationId || '' : undefined;
    return this.assignRole(addedRoles[i].roleId, addedRoles[i].leistungId, suborganization).pipe(
      map(() => errorList),
      catchError(err => of([...errorList, err])),
      switchMap(errors => this.callAddRoles(addedRoles, ++i, errors))
    );
  }

  private assignRole(roleId: string, cartridgeId: string, suborganization?: string): Observable<void> {
    const params = {
      roleId: roleId,
      cartridgeId: cartridgeId,
      organizationId: this.organizationId,
      suborganizationId: suborganization,
    };
    if (this.groupId) {
      return this.authorizer.assignRoleToGroup({ ...params, groupId: this.groupId });
    }
    if (this.userId) {
      return this.authorizer.assignRoleToUser({ ...params, userId: this.userId });
    }
    return of(void 0);
  }

  private removeRole(roleParams: {
    roleId: string;
    cartridgeId: string;
    suborganizationId?: string;
  }): Observable<void> {
    const params = {
      ...roleParams,
      leistungId: undefined,
      organizationId: this.organizationId,
    };
    if (this.groupId) {
      return this.authorizer.removeGroupFromRole({ ...params, groupId: this.groupId });
    }
    if (this.userId) {
      return this.authorizer.removeUserFromRole({ ...params, userId: this.userId });
    }
    return of(void 0);
  }

  private callRemoveRoles(
    removedRoles: UserCartridgeRoleAssignment[],
    i: number,
    errorList: Error[] = []
  ): Observable<Error[]> {
    if (removedRoles.length <= i) {
      return of(errorList);
    }
    return this.removeRole({ ...removedRoles[i], cartridgeId: removedRoles[i].leistungId }).pipe(
      map(() => errorList),
      catchError(err => of([...errorList, err])),
      switchMap(errors => this.callRemoveRoles(removedRoles, ++i, errors))
    );
  }

  private filterRemovedRoles(): UserCartridgeRoleAssignment[] {
    const removedRoles: UserCartridgeRoleAssignment[] = [];
    this.allPreviouslySelectedRoles.forEach(previouslySelectedRole => {
      const match = this.allCurrentlySelectedRoles.find(
        obj =>
          obj.roleId === previouslySelectedRole.roleId &&
          obj.organizationId === previouslySelectedRole.organizationId &&
          obj.leistungId === previouslySelectedRole.leistungId &&
          ((!obj.suborganizationId && !previouslySelectedRole.suborganizationId) ||
            obj.suborganizationId === previouslySelectedRole.suborganizationId)
      );
      if (!match) {
        removedRoles.push(previouslySelectedRole);
      }
    });
    return removedRoles;
  }

  private filterAddedRoles(): UserCartridgeRoleAssignment[] {
    const addedRoles: UserCartridgeRoleAssignment[] = [];
    this.allCurrentlySelectedRoles.forEach(currentlySelectedRole => {
      const matchesIgnoringSuborganization = this.allPreviouslySelectedRoles.filter(
        obj =>
          obj.roleId === currentlySelectedRole.roleId &&
          obj.organizationId === currentlySelectedRole.organizationId &&
          obj.leistungId === currentlySelectedRole.leistungId
      );
      if (currentlySelectedRole.suborganizationId === undefined) {
        if (!matchesIgnoringSuborganization.some(obj => !obj.suborganizationId)) {
          // If the currently selected role has no location restriction and was not previously selected it's an added role
          addedRoles.push(currentlySelectedRole);
        }
      } else {
        if (
          !matchesIgnoringSuborganization.some(obj => obj.suborganizationId === currentlySelectedRole.suborganizationId)
        ) {
          // If the currently selected role has a location restriction and was not previously selected it's an added role
          addedRoles.push(currentlySelectedRole);
        }
      }
    });
    return addedRoles;
  }
}
